<template>
  <el-drawer
    v-model="showProxy"
    class="modal-container"
    direction="rtl"
    :size="drawerSize"
    :with-header="true"
    :append-to-body="true"
    :modal-append-to-body="true"
    @close="handleClose()"
  >
    <!-- >>>>>> 弹窗头部（与运营管理抽屉等模块统一的纯标题样式） <<<<<< -->
    <template #header>
      <div class="drawer-header-unified">
        <span class="drawer-header-title">{{ $str.getKey('收益管理/录入电价') }}</span>
      </div>
    </template>

    <!-- >>>>>> 弹窗内容 <<<<<< -->
    <template #default>
      <div class="content-container" v-loading="loading">
        <!-- 模式选择：顶栏、无背景框，与下方卡片区分离 -->
        <div class="typein-mode-bar">
          <div class="mode-segment-wrap">
            <span class="mode-segment-label">模式选择</span>
            <div class="mode-segment" role="tablist" aria-label="模式选择">
              <button
                v-for="m in modeOptions"
                :key="m.value"
                type="button"
                class="mode-segment__item"
                :class="{ 'is-active': mode === m.value }"
                :disabled="m.disabled"
                @click="mode = m.value"
              >
                {{ m.label }}
              </button>
            </div>
          </div>
        </div>

        <!-- 非现货：筛选条件与现货「列表数据」区同结构（顶栏标签 + 控件全宽） -->
        <div v-if="mode !== 'spot'" class="typein-filter-panel">
          <div class="typein-filter-panel__head">
            <h3 class="typein-filter-panel__title">筛选条件</h3>
          </div>
          <el-form :model="formData" label-position="top" class="typein-filter-form" @submit.prevent>
            <div class="typein-filter-grid">
              <el-form-item :label="$str.getKey('收益管理/地区')">
                <el-cascader
                  class="typein-field-full"
                  :options="adOptions"
                  :props="formData.cascaderProps"
                  :show-all-levels="false"
                  clearable
                  placeholder="请选择"
                  @change="handleAdChange"
                />
              </el-form-item>
              <el-form-item :label="$str.getKey('收益管理/日期')">
                <el-date-picker
                  v-model="formData.month"
                  class="typein-field-full"
                  type="month"
                  :clearable="false"
                  @change="timeRangeChange"
                  :disabled-date="disabledDate"
                />
              </el-form-item>
            </div>
          </el-form>
        </div>

        <SpotPricePanel
          v-if="mode === 'spot'"
          ref="spotPanelRef"
          :ad-options="adOptions"
          :cascader-props="formData.cascaderProps"
        />

        <RecognizePricePanel
          v-else
          :form-data="formData"
          :row-items-big-industry="rowItemsBigIndustry"
          :row-items-general-business="rowItemsGeneralBusiness"
          :row-items-general-business-single="rowItemsGeneralBusinessSingle"
          :column-items="columnItems"
          :unit-options="origin.unitOptions"
          :segment-loading="segmentLoading"
          :price-loading="priceLoading"
          :preview-option="previewBarOptions[0] || emptyPreviewOption"
          :preview-options="previewBarOptions"
          :preview-titles="previewBarTitles"
          :on-img-change="handleImgchange"
          :on-before-upload="beforeImgUpload"
          :on-preview="handleImgPreview"
          :options-form-ref="optionsForm"
          :on-fetch-price="fetchRecognizedPrice"
          @fetchSegments="fetchProvinceSegments"
        />

        
        <!-- 预览图片：挂到顶层，避免被抽屉层级遮挡 -->
        <el-image-viewer
          v-if="isShowPreViewImg && formData.imgUrl"
          :url-list="[formData.imgUrl]"
          :z-index="9999"
          hide-on-click-modal
          @close="isShowPreViewImg = false"
        />
      </div>
    </template>

    <!-- >>>>>> 弹窗底部 <<<<<< -->
    <template #footer>
      <div class="footer-container">
        <el-button class="meter-feature-btn meter-feature-btn--lg" type="primary" @click="handleClose()">{{ $str.getKey('收益管理/关闭') }}</el-button>
      </div>
    </template>
  </el-drawer>
</template>
<script setup>
import { defineEmits, defineOptions, defineProps, onMounted, reactive, ref, nextTick, watch, computed, watchEffect } from "vue";
import { useVModel } from "@vueuse/core";
import { ElImageViewer, ElMessageBox } from 'element-plus';
import { View } from '@element-plus/icons-vue';
import dayjs from 'dayjs';
import * as echarts from 'echarts'
import { useAppStore } from "@/store/modules/app";
import { commitAnalyseOptions, commitElecPriceImg, queryDefaultAnalyseOptions } from "./api/index.js";
import { queryProvinceSegmentsInfo } from "@/api/business-operations";
import { cloneDeep } from "@/utils";
import { getElecPriceSegmentColor } from "@/utils/colorHelper/index.js";
import SpotPricePanel from './components/SpotPricePanel.vue'
import RecognizePricePanel from './components/RecognizePricePanel.vue'

const $str = window.$str;

const origin = {
  analyseItemList: [
    {
      name: $str.getKey('收益管理/尖峰段电价所在列'),
      key: 'sharpColumns',
      value: 0,
    },
    {
      name: $str.getKey('收益管理/高峰段电价所在列'),
      key: 'peakColumns',
      value: 0,
    },
    {
      name: $str.getKey('收益管理/平段电价所在列'),
      key: 'flatColumns',
      value: 0,
    },
    {
      name: $str.getKey('收益管理/低谷段电价所在列'),
      key: 'valleyColumns',
      value: 0,
    },
    {
      name: $str.getKey('收益管理/深谷段电价所在列'),
      key: 'deepColumns',
      value: 0,
    },
    {
      name: $str.getKey('收益管理/大工业10kv所在行'),
      key: 'bigIndustry10kvRows',
      value: 0,
    },
    {
      name: $str.getKey('收益管理/大工业20kv所在行'),
      key: 'bigIndustry20kvRows',
      value: 0,
    },
    {
      name: $str.getKey('收益管理/大工业35kv所在行'),
      key: 'bigIndustry35kvRows',
      value: 0,
    },
    {
      name: $str.getKey('收益管理/大工业110kv所在行'),
      key: 'bigIndustry110kvRows',
      value: 0,
    },
    {
      name: $str.getKey('收益管理/大工业220kv所在行'),
      key: 'bigIndustry220kvRows',
      value: 0,
    },
    {
      name: $str.getKey('收益管理/一般工商业两部制1KV所在行'),
      key: 'generalBusiness1kvRows',
      value: 0,
    },
    {
      name: $str.getKey('收益管理/一般工商业两部制10KV所在行'),
      key: 'generalBusiness10kvRows',
      value: 0,
    },
    {
      name: $str.getKey('收益管理/一般工商业两部制20KV所在行'),
      key: 'generalBusiness20kvRows',
      value: 0,
    },
    {
      name: $str.getKey('收益管理/一般工商业两部制35KV所在行'),
      key: 'generalBusiness35kvRows',
      value: 0,
    },
    {
      name: $str.getKey('收益管理/一般工商业两部制110KV所在行'),
      key: 'generalBusiness110kvRows',
      value: 0,
    },
    {
      name: $str.getKey('收益管理/一般工商业两部制220KV所在行'),
      key: 'generalBusiness220kvRows',
      value: 0,
    },

    {
      name: $str.getKey('收益管理/一般工商业单一制1KV所在行'),
      key: 'singleGeneralBusiness1kvRows',
      value: 0,
    },
    {
      name: $str.getKey('收益管理/一般工商业单一制10KV所在行'),
      key: 'singleGeneralBusiness10kvRows',
      value: 0,
    },
    {
      name: $str.getKey('收益管理/一般工商业单一制20KV所在行'),
      key: 'singleGeneralBusiness20kvRows',
      value: 0,
    },
    {
      name: $str.getKey('收益管理/一般工商业单一制35KV所在行'),
      key: 'singleGeneralBusiness35kvRows',
      value: 0,
    },
    {
      name: $str.getKey('收益管理/一般工商业单一制110KV所在行'),
      key: 'singleGeneralBusiness110kvRows',
      value: 0,
    },
    {
      name: $str.getKey('收益管理/一般工商业单一制220KV所在行'),
      key: 'singleGeneralBusiness220kvRows',
      value: 0,
    },


    {
      name: $str.getKey('收益管理/电价单位'),
      key: 'priceUnit',
      value: 1, // ['1-元', '2-角', '3-分'],
    },
  ],
  unitOptions: [
    {
      value: 1,
      label: $str.getKey('收益管理/元'),
    },
    {
      value: 2,
      label: $str.getKey('收益管理/角'),
    },
    {
      value: 3,
      label: $str.getKey('收益管理/分'),
    },
  ],
}
 

const stationId = computed(() => {
  return useAppStore().stationId
})

/** 设置组件配置项 */
defineOptions({
  name: "TypeInModal"
});

/** 接收父组件信息 */
const props = defineProps({
  show: {
    type: Boolean,
    default: false
  },
  adOptions: {
    type: Array,
    default: []
  },
});

const emit = defineEmits(['update:show', 'onClose']);
const showProxy = useVModel(props, "show", emit);
const drawerSize = computed(() => {
  const width = window.innerWidth
  if (width <= 960) return '96%'
  if (width <= 1366) return '90%'
  return '70%'
})
const isConfirming = ref(false); // 防抖【连续多次点击提交按钮】
const optionsForm = ref(null); // 引用配置表单DOM
const spotPanelRef = ref(null)
const adOptions = computed(() => props.adOptions); // 行政区域的级联选项
const loading = ref(false); // 控制加载中动效的渲染

const isShowPreViewImg = ref(false);
const mode = ref('proxy');
const modeOptions = [
  { value: 'proxy', label: '代理购电电价', disabled: false },
  { value: 'spot', label: '现货电价', disabled: false },
  { value: 'chargePile', label: '充电桩电价', disabled: false },
]

// 后端 `priceType`：1 代理购电；2 充电桩；现货不传 priceType
const modeToPriceType = {
  proxy: 1,
  chargePile: 2,
}

const formData = reactive({
  cascaderProps: { // 级联配置对象
    checkStrictly: true,
    expandTrigger: 'hover',
    emitPath: false, // 只返回当前节点的值
  },
  adCode: '', // 示例：山东省-370000
  month: dayjs().format('YYYY-MM'),
  imgFile: null,
  imgRawData: null,
  imgUrl: '',
  optionFormData: createOptionFormData(origin.analyseItemList),
})

// 现货电价 UI 逻辑已拆分到 SpotPricePanel

const columnKeys = new Set(['sharpColumns', 'peakColumns', 'flatColumns', 'valleyColumns', 'deepColumns', 'priceUnit'])
const columnItems = computed(() => origin.analyseItemList.filter(it => columnKeys.has(it.key)))
const rowItems = computed(() => origin.analyseItemList.filter(it => !columnKeys.has(it.key)))

const getVoltageLabelFromKey = (key) => {
  const m = String(key || '').match(/(\d+kv)/i)
  return m ? m[1].toLowerCase() : '--'
}

const rowItemsBigIndustry = computed(() => {
  return rowItems.value
    .filter(it => String(it.key).startsWith('bigIndustry'))
    .map(it => ({ ...it, shortLabel: getVoltageLabelFromKey(it.key) }))
})

const rowItemsGeneralBusiness = computed(() => {
  return rowItems.value
    .filter(it => String(it.key).startsWith('generalBusiness'))
    .map(it => ({ ...it, shortLabel: getVoltageLabelFromKey(it.key) }))
})

const rowItemsGeneralBusinessSingle = computed(() => {
  return rowItems.value
    .filter(it => String(it.key).startsWith('singleGeneralBusiness'))
    .map(it => ({ ...it, shortLabel: getVoltageLabelFromKey(it.key) }))
})

const previewPriceInfo = ref({
  sharpPrice: null,
  peakPrice: null,
  flatPrice: null,
  valleyPrice: null,
  deepValleyPrice: null,
  unit: '元/kWh',
})

const segmentLoading = ref(false)
/** 省份时段规则（可能多套） */
const provinceSegmentsList = ref([])
const priceLoading = ref(false)
let defaultOptionsReqSeq = 0

function resetCurrentModeInputs() {
  if (mode.value === 'spot') return
  formData.imgRawData = null
  formData.imgUrl = ''
  formData.optionFormData = createOptionFormData(origin.analyseItemList)
}

async function loadDefaultOptionsForCurrentMode() {
  if (mode.value === 'spot') return
  if (!formData.adCode) return
  const reqId = ++defaultOptionsReqSeq
  const modeSnapshot = mode.value
  const adCodeSnapshot = formData.adCode
  const priceType = modeToPriceType[modeSnapshot] ?? 1
  try {
    const res = await queryDefaultAnalyseOptions(adCodeSnapshot, 0, priceType)
    // 防止切换入口/地区后旧请求回写
    if (reqId !== defaultOptionsReqSeq) return
    if (mode.value !== modeSnapshot) return
    if (formData.adCode !== adCodeSnapshot) return
    const data = res?.data
    if (!data) return
    const next = createOptionFormData(origin.analyseItemList)
    origin.analyseItemList.forEach((it) => {
      next[it.key] = data[it.key] ?? next[it.key]
    })
    formData.optionFormData = next
  } catch (e) {
    // ignore
  }
}

const toProvinceCode = (adCode) => {
  const code = String(adCode ?? '').trim()
  if (!code) return ''
  // 已经是省级（常见：XX0000 或 XX000000）
  if (/^\d{2}0{4}$/.test(code)) return code
  if (/^\d{2}0{6}$/.test(code)) return code.slice(0, 2) + '0000'
  // 市/区县：取前 2 位补 0000
  if (/^\d{6}$/.test(code)) return code.slice(0, 2) + '0000'
  // 兜底：尽量截取前 2 位
  if (/^\d{2}/.test(code)) return code.slice(0, 2) + '0000'
  return code
}

const timeStrToHour = (t) => {
  const s = String(t ?? '')
  const m = s.match(/^(\d{1,2}):(\d{2})$/)
  if (!m) return NaN
  const hh = Number(m[1])
  const mm = Number(m[2])
  if (!Number.isFinite(hh) || !Number.isFinite(mm)) return NaN
  return hh + mm / 60
}

async function fetchProvinceSegments() {
  const provinceCode = toProvinceCode(formData.adCode)
  const year = dayjs(formData.month || dayjs()).format('YYYY')
  const month = dayjs(formData.month || dayjs()).format('MM')
  if (!provinceCode) {
    window.dispatchNotify?.({ type: 'warning', message: '请先选择地区' })
    return
  }
  segmentLoading.value = true
  try {
    const res = await queryProvinceSegmentsInfo(year, month, provinceCode)
    const ruleList = Array.isArray(res?.data?.ruleList) ? res.data.ruleList : []
    const list = ruleList.map(r => r?.ruleInfo).filter(Boolean)
    if (!list.length) {
      provinceSegmentsList.value = []
      window.dispatchNotify?.({ type: 'warning', message: '未获取到该省份时段信息' })
      return
    }
    provinceSegmentsList.value = list
  } catch (e) {
    provinceSegmentsList.value = []
    window.dispatchNotify?.({ type: 'warning', message: '获取时段失败' })
  } finally {
    segmentLoading.value = false
  }
}

const buildPreviewOptionForRule = (ruleInfo) => {
  const hasRecognizedPrice = [
    previewPriceInfo.value.sharpPrice,
    previewPriceInfo.value.peakPrice,
    previewPriceInfo.value.flatPrice,
    previewPriceInfo.value.valleyPrice,
    previewPriceInfo.value.deepValleyPrice,
  ].some(v => Number.isFinite(Number(v)));

  // 仅获取时段（未识别电价）时，使用可视化兜底高度展示分段，避免整图“空白”
  const fallbackPreviewValue = {
    sharp: 1.0,
    peak: 0.8,
    flat: 0.6,
    valley: 0.4,
    deep: 0.2,
  };
  const resolvePreviewValue = (segmentKey, priceVal) => {
    if (hasRecognizedPrice) {
      return Number.isFinite(Number(priceVal)) ? Number(priceVal) : 0;
    }
    return fallbackPreviewValue[segmentKey];
  };

  const seg = {
   
    sharp: { key: 'sharp', legend: '尖', name: '尖峰时段', value: resolvePreviewValue('sharp', previewPriceInfo.value.sharpPrice), color: getElecPriceSegmentColor('sharp') },
    peak: { key: 'peak', legend: '峰', name: '高峰时段', value: resolvePreviewValue('peak', previewPriceInfo.value.peakPrice), color: getElecPriceSegmentColor('peak') },
    flat: { key: 'flat', legend: '平', name: '平时段', value: resolvePreviewValue('flat', previewPriceInfo.value.flatPrice), color: getElecPriceSegmentColor('flat') },
    valley: { key: 'valley', legend: '谷', name: '低谷时段', value: resolvePreviewValue('valley', previewPriceInfo.value.valleyPrice), color: getElecPriceSegmentColor('valley') },
    deep: { key: 'deep', legend: '深谷', name: '深谷时段', value: resolvePreviewValue('deep', previewPriceInfo.value.deepValleyPrice), color: getElecPriceSegmentColor('deepValley') },
  }

  const buildTimelineFromSegments = (ruleInfo) => {
    const keyMap = {
      sharp: 'sharp',
      peak: 'peak',
      flat: 'flat',
      valley: 'valley',
      deepValley: 'deep',
    }
    const out = []
    Object.entries(keyMap).forEach(([k, segKey]) => {
      const list = ruleInfo?.[k]
      if (!Array.isArray(list)) return
      list.forEach((range) => {
        const st = Array.isArray(range) ? range[0] : null
        const et = Array.isArray(range) ? range[1] : null
        const start = timeStrToHour(st)
        const end = timeStrToHour(et)
        if (!Number.isFinite(start) || !Number.isFinite(end)) return
        out.push({ start, end: end === 0 ? 24 : end, ...seg[segKey] })
      })
    })
    // 排序并裁剪到 [0,24]
    return out
      .map(it => ({ ...it, start: Math.max(0, Math.min(24, it.start)), end: Math.max(0, Math.min(24, it.end)) }))
      .filter(it => it.end > it.start)
      .sort((a, b) => a.start - b.start)
  }

  // 默认兜底：0~24 均分 5 段
  const timelineFallback = [
    { start: 0, end: 5, ...seg.deep },
    { start: 5, end: 10, ...seg.valley },
    { start: 10, end: 15, ...seg.flat },
    { start: 15, end: 20, ...seg.peak },
    { start: 20, end: 24, ...seg.sharp },
  ]

  const timeline = ruleInfo ? (buildTimelineFromSegments(ruleInfo) || timelineFallback) : timelineFallback

  const timelineData = timeline.map(t => ({
    name: t.name,
    value: [t.start, t.end, t.value, t.name, t.color, t.key, t.legend],
    itemStyle: { color: t.color },
  }))

  const legendOrder = [seg.sharp.legend, seg.peak.legend, seg.flat.legend, seg.valley.legend, seg.deep.legend]
  const legendColorMap = {
    [seg.sharp.legend]: seg.sharp.color,
    [seg.peak.legend]: seg.peak.color,
    [seg.flat.legend]: seg.flat.color,
    [seg.valley.legend]: seg.valley.color,
    [seg.deep.legend]: seg.deep.color,
  }

  return /** @type {import('echarts').EChartsOption} */ ({
    // custom 系列的 legend 色块有时不会跟随 renderItem 的 fill，这里显式指定调色盘，保证一致
    color: legendOrder.map((n) => legendColorMap[n]),
    grid: { left: 58, right: 22, top: 54, bottom: 40, containLabel: true },
    legend: {
      top: 10,
      left: 'center',
      data: legendOrder,
      itemWidth: 10,
      itemHeight: 10,
      textStyle: { color: 'rgba(46,46,46,0.72)', fontSize: 12 },
    },
    tooltip: {
      trigger: 'item',
      formatter: (p) => {
        const v = Array.isArray(p?.data?.value) ? p.data.value : []
        const start = v[0]
        const end = v[1]
        const price = v[2]
        const name = v[3] ?? p?.name ?? ''
        return `${name}<br/>时间：${start}:00 - ${end}:00<br/>电价：${price} ${previewPriceInfo.value.unit}`
      },
    },
    xAxis: {
      type: 'value',
      min: 0,
      max: 24,
      interval: 6,
      axisTick: { show: true },
      axisLine: { lineStyle: { color: 'rgba(0,0,0,0.18)' } },
      axisLabel: {
        color: 'rgba(46,46,46,0.75)',
        formatter: (v) => `${v}:00`,
      },
    },
    yAxis: {
      type: 'value',
      name: `电价(${previewPriceInfo.value.unit})`,
      nameTextStyle: { color: 'rgba(46,46,46,0.55)' },
      nameGap: 14,
      splitLine: { lineStyle: { color: 'rgba(0,0,0,0.06)' } },
      axisLabel: { color: 'rgba(46,46,46,0.6)', margin: 10 },
    },
    series: [
      ...legendOrder.map((legendName) => {
        const data = timelineData
          .filter(it => (it?.value?.[6] === legendName))
          .map(it => ({
            ...it,
            itemStyle: { ...(it.itemStyle || {}), color: legendColorMap[legendName] },
          }))

        return {
          name: legendName,
          type: 'custom',
          data,
          renderItem: (params, api) => {
            const start = api.value(0)
            const end = api.value(1)
            const price = api.value(2)

            const x0 = api.coord([start, price])[0]
            const x1 = api.coord([end, price])[0]
            const y0 = api.coord([start, 0])[1]
            const y1 = api.coord([start, price])[1]

            const rect = echarts.graphic.clipRectByRect(
              {
                x: x0,
                y: y1,
                width: x1 - x0,
                height: y0 - y1,
              },
              {
                x: params.coordSys.x,
                y: params.coordSys.y,
                width: params.coordSys.width,
                height: params.coordSys.height,
              }
            )

            return rect && {
              type: 'rect',
              shape: rect,
              style: api.style({
                fill: api.visual('color'),
                opacity: 0.8,
              }),
            }
          },
          encode: { x: [0, 1], y: 2 },
          itemStyle: {
            color: legendColorMap[legendName],
            borderRadius: [2, 2, 0, 0],
          },
        }
      }),
    ]
  })
}

const previewBarOptions = computed(() => {
  const list = Array.isArray(provinceSegmentsList.value) ? provinceSegmentsList.value : []
  if (!list.length) return []
  return list.map((ruleInfo) => buildPreviewOptionForRule(ruleInfo))
})

const previewBarTitles = computed(() => {
  const list = Array.isArray(provinceSegmentsList.value) ? provinceSegmentsList.value : []
  if (!list.length) return []
  return list.map((_, idx) => `时段规则 ${idx + 1}`)
})

const emptyPreviewOption = computed(() => {
  return /** @type {import('echarts').EChartsOption} */ ({
    grid: { left: 20, right: 20, top: 20, bottom: 20 },
    xAxis: { show: false, type: 'value' },
    yAxis: { show: false, type: 'value' },
    series: [],
    graphic: [
      {
        type: 'text',
        left: 'center',
        top: 'middle',
        style: {
          text: provinceSegmentsList.value?.length ? '' : '未获取到该省份时段信息\n请检查地区/月份后重试',
          fill: 'rgba(46,46,46,0.55)',
          fontSize: 12,
          lineHeight: 18,
          align: 'center',
        },
      },
    ],
  })
})




// 初始化面板
initPanel();

watch(
  mode,
  async (nv) => {
    // 入口切换：清空上一入口输入，避免串数据；再按新 priceType 回显默认配置
    if (nv !== 'spot') {
      resetCurrentModeInputs()
      previewPriceInfo.value = {
        sharpPrice: null,
        peakPrice: null,
        flatPrice: null,
        valleyPrice: null,
        deepValleyPrice: null,
        unit: '元/kWh',
      }
      await loadDefaultOptionsForCurrentMode()
    }
  },
  { immediate: true }
)





/** ====== [业务：功能方法] =================================================================== */
/** 关闭面板 */
function handleClose(needRefresh = false) {
  const priceInfo = {
    adCode: formData.adCode,
    month: formData.month,
  }
  // 通知父组件关闭当前弹窗。 参数: 父组件是否需要刷新数据。 
  emit("onClose", needRefresh, priceInfo);
}

/** 事件响应：行政区域发生变化 */
function handleAdChange(adCode) {
  formData.adCode = adCode;
  resetCurrentModeInputs()
  loadDefaultOptionsForCurrentMode()
}

watch(
  () => [formData.adCode, formData.month, mode.value],
  async ([adCode, month, m]) => {
    if (!adCode || !month) return

    // 地区/月/入口变化时：回显当前入口的默认配置（按 priceType 区分）
    if (m !== 'spot') {
      await loadDefaultOptionsForCurrentMode()
    }
  },
  { immediate: true }
)

// 旧版“抽屉确定提交”流程已废弃：现在由「配置获取电价」按钮驱动识别与回显图表

function pickPriceFromRecognizeResponse(res) {
  const d = res?.data ?? res
  // 1) 直接返回尖峰平谷字段
  if (d && typeof d === 'object' && !Array.isArray(d)) {
    const sharpPrice = d.sharpPrice ?? d.sharp ?? d.sharp_price
    const peakPrice = d.peakPrice ?? d.peak ?? d.peak_price
    const flatPrice = d.flatPrice ?? d.flat ?? d.flat_price
    const valleyPrice = d.valleyPrice ?? d.valley ?? d.valley_price
    const deepPrice = d.deepPrice ?? d.deepValleyPrice ?? d.deep ?? d.deep_price
    const hasAny =
      [sharpPrice, peakPrice, flatPrice, valleyPrice, deepPrice].some(v => v !== undefined && v !== null)
    if (hasAny) {
      return {
        sharpPrice,
        peakPrice,
        flatPrice,
        valleyPrice,
        deepValleyPrice: deepPrice,
      }
    }
  }

  // 2) 返回列表：挑一条用于回填；it.type 为单一制/两部制（0/1），与 industry 工业类型无关
  // 优先：单一制 type=0 且 10kv
  const list = Array.isArray(d) ? d : Array.isArray(d?.rows) ? d.rows : Array.isArray(d?.data) ? d.data : []
  if (Array.isArray(list) && list.length) {
    const pick =
      list.find(it => it?.type === 0 && String(it?.voltageLevel || '').toLowerCase() === '10kv') ||
      list.find(it => it?.type === 0) ||
      list[0]
    return {
      sharpPrice: pick?.sharpPrice,
      peakPrice: pick?.peakPrice,
      flatPrice: pick?.flatPrice,
      valleyPrice: pick?.valleyPrice,
      deepValleyPrice: pick?.deepPrice ?? pick?.deepValleyPrice,
    }
  }

  return null
}

async function fetchRecognizedPrice() {
  if (mode.value === 'spot') return

  const fdParam = new FormData()
  const priceType = modeToPriceType[mode.value] ?? 1
  // 识别接口 `type`：按后端约定固定为 0
  fdParam.append('type', 0)
  // `/schedule/job/state/grid/price/image/recognize` 需要区分业务类型
  fdParam.append('priceType', String(priceType))
  const optParams = { type: 0, priceType }

  // 必填校验：地区/月/图片/配置
  let ok = verifyAdCode(fdParam, optParams)
  if (!ok) {
    window.dispatchNotify({ type: 'warning', message: $str.getKey('收益管理/请选择区域！') })
    return
  }
  ok = verifyTime(fdParam)
  if (!ok) {
    window.dispatchNotify({ type: 'warning', message: $str.getKey('收益管理/请选择时间！') })
    return
  }
  ok = verifyImgFile(fdParam)
  if (!ok) {
    window.dispatchNotify({ type: 'warning', message: $str.getKey('收益管理/请上传电价图片！') })
    return
  }
  ok = await verifyOptionItems(optParams)
  if (!ok) {
    window.dispatchNotify({ type: 'warning', message: $str.getKey('收益管理/请正确填写配置项信息！') })
    return
  }

  priceLoading.value = true
  try {
    await commitAnalyseOptions(optParams)
    const res = await commitElecPriceImg(fdParam)
    const picked = pickPriceFromRecognizeResponse(res)
    if (!picked) {
      window.dispatchNotify?.({ type: 'warning', message: '未获取到电价数据，请检查配置与图片' })
      return
    }
    previewPriceInfo.value = {
      sharpPrice: picked.sharpPrice,
      peakPrice: picked.peakPrice,
      flatPrice: picked.flatPrice,
      valleyPrice: picked.valleyPrice,
      deepValleyPrice: picked.deepValleyPrice,
      unit: '元/kWh',
    }
    // 识别成功后自动拉取时段并渲染图表（接口无返回时将显示“未获取到时段”提示）
    await fetchProvinceSegments()
    window.dispatchNotify?.({ type: 'success', message: '电价已回显到图表' })
  } catch (e) {
    window.dispatchNotify?.({ type: 'warning', message: '获取电价失败，请检查配置与图片' })
  } finally {
    priceLoading.value = false
  }
}


/** 校验: 是否选择了行政区域 */
function verifyAdCode(fdParam, optParams) {
  if (formData.adCode) {
    fdParam.append('areaCode', formData.adCode); 
    optParams['areaCode'] = formData.adCode;
    return true;
  }
  return false;
}


/** 校验: 是否选择了月份 */
function verifyTime(fdParam) {
  if (formData.month) {
    const month = dayjs(formData.month).format('YYYY-MM')
    fdParam.append('month', month); 

    return true;
  }
  return false;
}


/** 校验: 是否上传了图片 */
function verifyImgFile(fdParam) {
  if (formData.imgRawData) {
    // 调用接口时，file对应的信息应该是upload组件加载到的【uploadFile.raw】
    fdParam.append('file', formData.imgRawData); 
    return true;
  }

  return false;
}

/** 校验: 配置项 */
async function verifyOptionItems(params) {
  // `optionsForm` 由子组件内部的列配置表单 ref 回填；首次渲染/切换模式时可能尚未就绪
  await nextTick()
  const pt = modeToPriceType[mode.value] ?? 1
  const form = optionsForm.value
  const canValidate = form && typeof form.validate === 'function'
  if (!canValidate) {
    Object.assign(params, formData.optionFormData)
    params.priceType = pt
    params.type = 0
    return true
  }

  const isOk = await form
    .validate()
    .then(() => true)
    .catch(() => false)
  if (isOk) Object.assign(params, formData.optionFormData)
  // 合并表单后强制业务维度，避免将来表单字段覆盖 priceType / type
  params.priceType = pt
  params.type = 0
  return isOk
}



/** 设置日期的禁选范围 */
function disabledDate(date) {
  return false;
  // return date < dayjs().subtract(6, 'month').startOf('month');
}

/** 响应选择月份发生变化 */
function timeRangeChange() {
  // 请求数据，更新回显内容：电价图&生成的电价数据
}

/** 事件响应：加载到的文件发生变化 */
function handleImgchange(uploadFile, uploadFiles) {
  const file = uploadFile.raw; // 获取上传的文件
  if (checkFileIsImg(file)) {
    formData.imgRawData = file;
    const reader = new FileReader(); // 使用 FileReader 读取并预览图片
    reader.onload = (e) => {
      formData.imgUrl = e.target.result; // 将图片设置为预览地址
    }
    reader.readAsDataURL(file)
  }
}


/** 事件响应：加载文件之前的钩子--校验文件正确性 */
function beforeImgUpload(rawFile) {
  // 检查文件类型
  if (!checkFileIsImg(rawFile)) {
    window.dispatchNotify({
      type: "warning",
      message: $str.getKey('收益管理/只能上传图片文件!')
    });
    return false
  }
  
  // 检查文件大小（可选）
  if (rawFile.size / 1024 / 1024 > 20) {
    window.dispatchNotify({
      type: "warning",
      message: $str.getKey('收益管理/图片大小不能超过 20MB!')
    });
    return false
  }
  
  return true
}


/** 事件响应：预览图片 */
function handleImgPreview(event) {
  event.stopPropagation();
  isShowPreViewImg.value = true;
}


/** ====== [辅助：功能方法] =================================================================== */
/** 初始化：面板信息 */
function initPanel() {}



/** 格式化行政区域下拉选项的数据结构 */
function formatCascaderOption(list) {
  if (Array.isArray(list)) {
    return list.map(item => {
      const newItem = {
        value: item.code,
        label: item.name,
        children: [],
      }
      if (Array.isArray(item.cityList)) {
        newItem.children = formatCascaderOption(item.cityList);
      }
      return newItem;
    })
  }
  console.error('接口返回的行政区域选项应为数组！')
  return [];
}


/** 校验方法：文件是否为图片 */
function checkFileIsImg(file) {
  const imgTypeList = ['image/jpeg', 'image/png', 'image/svg', 'image/gif'];
  const isImg = imgTypeList.some(type => file.type.startsWith(type));
  return isImg;
}


/** 创建: 配置项表单对应的数据对象 */
function createOptionFormData(itemList) {
  const cfg = {};
  itemList.forEach(it => {
    cfg[it.key] = it.value;
  })
  return cfg;
}




</script>

<style lang="scss" scoped>
@import '../_shared/feature-button.scss';

.modal-container {
  :deep(.el-drawer__header) {
    margin-bottom: 0;
    padding: 16px 20px;
    border-bottom: 1px solid rgba(0, 0, 0, 0.06);
    background: #fff;
  }

  :deep(.el-drawer__body) {
    padding: 0;
    height: 100%;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    background: #f0f2f5;
  }

  :deep(.el-drawer__footer) {
    padding: 12px 20px;
    border-top: 1px solid rgba(0, 0, 0, 0.06);
    background: #fff;
  }

  .footer-container {
    display: flex;
    justify-content: flex-end;
  }

  /* 底部按钮：统一使用 .meter-feature-btn */

  .drawer-header-unified {
    width: 100%;
    min-width: 0;
  }

  .drawer-header-title {
    font-size: 16px;
    font-weight: 600;
    color: #2e2e2e;
    line-height: 1.4;
  }

  .content-container {
    & {
      width: 100%;
      flex: 1;
      box-sizing: border-box;
      padding: 8px 14px 16px;
      overflow: auto;
      overflow-x: hidden;
      display: flex;
      flex-direction: column;
      gap: 16px;
    }

    /* 顶栏：仅模式切换，无外层灰底框 */
    .typein-mode-bar {
      display: flex;
      flex-direction: column;
      gap: 0;
      flex-shrink: 0;
    }

    .mode-segment-wrap {
      display: flex;
      align-items: center;
      gap: 10px;
      flex-wrap: wrap;
    }

    .mode-segment-label {
      font-size: 12px;
      font-weight: 600;
      color: rgba(46, 46, 46, 0.55);
      flex-shrink: 0;
      letter-spacing: 0.02em;
    }

    .mode-segment {
      display: inline-flex;
      align-items: center;
      flex-wrap: wrap;
      gap: 8px;
      padding: 0;
      background: transparent;
      box-shadow: none;
    }

    .mode-segment__item {
      margin: 0;
      border: 1px solid rgba(0, 0, 0, 0.1);
      background: #fff;
      color: rgba(46, 46, 46, 0.78);
      font-size: 13px;
      font-weight: 500;
      padding: 8px 18px;
      min-height: 36px;
      border-radius: 8px;
      cursor: pointer;
      user-select: none;
      transition: border-color 0.2s ease, background 0.2s ease, color 0.2s ease, box-shadow 0.2s ease;
    }

    .mode-segment__item:hover:not(:disabled):not(.is-active) {
      border-color: rgba(58, 104, 255, 0.45);
      color: rgba(58, 104, 255, 0.95);
      background: rgba(58, 104, 255, 0.06);
    }

    .mode-segment__item.is-active {
      background: #3a68ff;
      border-color: #3a68ff;
      color: #fff;
      box-shadow: 0 2px 10px rgba(58, 104, 255, 0.25);
    }

    .mode-segment__item:disabled {
      cursor: not-allowed;
      opacity: 0.45;
    }

    /* 与 SpotPricePanel「列表数据」面板一致 */
    .typein-filter-panel {
      border: 1px solid rgba(0, 0, 0, 0.06);
      border-radius: 20px;
      padding: 14px 20px 18px;
      background: rgba(255, 255, 255, 0.5);
      backdrop-filter: blur(10px);
      box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
      flex-shrink: 0;
    }

    .typein-filter-panel__head {
      margin-bottom: 12px;
    }

    .typein-filter-panel__title {
      margin: 0;
      font-size: 16px;
      font-weight: 600;
      color: #2e2e2e;
    }

    .typein-filter-grid {
      display: grid;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: 12px 20px;
      max-width: 640px;
    }

    @media (max-width: 720px) {
      .typein-filter-grid {
        grid-template-columns: 1fr;
      }
    }

    .typein-filter-form :deep(.el-form-item) {
      margin-bottom: 0;
    }

    .typein-filter-form :deep(.el-form-item__label) {
      font-size: 12px;
      color: rgba(46, 46, 46, 0.65);
      line-height: 1.3;
      margin-bottom: 6px;
      padding: 0;
    }

    .typein-field-full {
      width: 100%;
    }

    .typein-filter-form :deep(.el-input__wrapper),
    .typein-filter-form :deep(.el-select__wrapper) {
      border-radius: 6px;
    }

    .main-row {
      display: flex;
      gap: 14px;
      align-items: stretch;
    }

    .left-container {
      /* 左侧上传区缩窄，给右侧字段区更多空间，避免遮挡 */
      width: clamp(320px, 28vw, 420px);
      flex: 0 0 auto;
      padding: 14px;
      border-radius: 2px;
      background: #ffffff;
      border: 1px solid rgba(0, 0, 0, 0.08);
      display: flex;
      flex-direction: column;
      gap: 10px;

      .img-container {
        // flex: 1;

        .img-uploader {
          height: var(--upload-h, clamp(200px, 22vh, 260px));
          width: 100%;

          ::v-deep .el-upload {
            &.el-upload--text {
              width: 100%;
              height: 100%;
              border: 1px dashed #98A2BB;
              border-radius: 2px;
              cursor: pointer;
              position: relative;
              overflow: hidden;
              transition: var(--el-transition-duration-fast);
            }

            &:hover {
              border-color: #5165E1;
            }

            .el-upload-dragger {
              & {
                height: 100%;
                border-width: 0;
                padding: 0;
                background: transparent;
              }

              .preview-container {
                width: 100%;
                height: 100%;
                position: relative;

                .elec-price-img {
                  display: block;
                  width: 100%;
                  height: 100%;
                }

                .btn-container {
                  position: absolute;
                  top: 50%;
                  left: 50%;
                }
              }

              .icon-info-container {
                width: 100%;
                height: 100%;
                margin: 0;

                .uploader-icon {
                  width: 100%;
                  height: 100%;
                  font-size: 28px;
                  color: #5165E1;
                  text-align: center;
                }

                .tip-text {
                  font-size: 14px;
                  color: #98A2BB;
                  position: absolute;
                  top: calc(50% + 24px);
                  left: 0;
                  right: 0;
                }
              }
            }
          }
        }
      }

      .tip-container {
        width: 100%;
        height: 32px;
        display: flex;
        align-items: center;
        gap: 6px;
        color: #c6b07c;
      }
    }

    .right-stack {
      flex: 1;
      min-width: 0;
      display: flex;
      flex-direction: column;
      gap: 10px;
    }

    .right-stack--top {
      /* 让上方右侧卡片高度与左侧上传区一致 */
      align-self: stretch;
    }

    .right-card--fill {
      height: 100%;
      display: flex;
      flex-direction: column;
      justify-content: flex-start;
      overflow: hidden;
    }

    .right-card {
      border-radius: 2px;
      background: #ffffff;
      border: 1px solid rgba(0, 0, 0, 0.08);
      padding: 14px;
      display: flex;
      flex-direction: column;
      gap: 10px;
      min-height: 0;
      overflow-x: hidden;
    }

    .right-title {
      font-size: 14px;
      font-weight: 600;
      color: rgba(46, 46, 46, 0.85);
    }

    .row-subtitle {
      font-size: 13px;
      font-weight: 600;
      color: rgba(46, 46, 46, 0.7);
      margin-top: 2px;
    }

    .options-grid {
      margin: 0;
      display: grid;
      /* 宽度不够自动换行，避免遮挡/横向滚动条 */
      grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
      column-gap: 14px;
      row-gap: 6px;
    }

    .options-grid--rows {
      /* 行配置项固定 3 列排布 */
      grid-template-columns: 1fr;
    }

    .options-grid :deep(.el-form-item) {
      margin-bottom: 0;
    }

    /* 让表单项内容可收缩，避免撑出横向滚动 */
    .options-grid :deep(.el-form-item__content) {
      min-width: 0;
      flex: 1;
    }

    .options-grid :deep(.el-select) {
      width: 100%;
    }

    /* 行配置：输入框收窄，避免占用过多空间 */
    .options-grid--rows :deep(.el-input-number) {
      width: 140px;
    }

    /* 列配置：保持占满，便于阅读长 label */
    .options-grid--cols :deep(.el-input-number) {
      width: 100%;
    }

  }

  .cols-row {
    padding: 0 12px;
  }

  .cols-title {
    font-size: 14px;
    font-weight: 600;
    color: rgba(46, 46, 46, 0.85);
    margin-bottom: 8px;
  }

  .cols-card {
    background: #ffffff;
    border: 1px solid rgba(0, 0, 0, 0.08);
    border-radius: 2px;
    padding: 12px 14px;
    overflow-x: hidden;
  }

  .rows-grid {
    display: grid;
    /* 三个模块固定三列并排展示 */
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 10px;
    overflow: auto;
    overflow-x: hidden;
    padding-right: 2px;
  }

  .rows-grid--in-top {
    /* 放在顶部右侧卡片内：跟左侧上传区同高，内容超出则在卡片内纵向滚动 */
    flex: 1;
    min-height: 0;
  }

  .rows-module {
    background: #ffffff;
    border: 1px solid rgba(0, 0, 0, 0.08);
    border-radius: 2px;
    padding: 12px 14px;
    overflow-x: hidden;
  }

  .rows-module--disabled {
    opacity: 0.7;
  }

  .rows-empty {
    font-size: 12px;
    color: rgba(46, 46, 46, 0.55);
    padding: 10px 0 4px;
  }

  .preview-row {
    padding: 0 12px 8px;
  }

  .preview-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    margin-bottom: 8px;
  }

  .preview-title {
    font-size: 14px;
    font-weight: 600;
    color: rgba(46, 46, 46, 0.85);
  }

  .preview-panel {
    height: 240px;
    background: #ffffff;
    border: 1px solid rgba(0, 0, 0, 0.08);
    border-radius: 2px;
    overflow: hidden;
  }

  .preview-chart {
    width: 100%;
    height: 100%;
  }


}
</style>
